/**
 * Status Effect System (M18)
 *
 * Manages status effects on entities (Player and Enemy).
 * Each entity stores a Map of active effects: effectId -> { timer, stacks, tickTimer }
 *
 * Effects:
 * - burning: 2 damage/sec for 3s. Orange flicker.
 * - frozen: 60% slow for 3s, next hit +50% damage then clears. Blue tint.
 * - poisoned: 1 damage/sec for 6s, stacks. Green tint.
 * - stunned: Cannot act for 1.5s. Yellow flash.
 */

export const STATUS_EFFECTS = {
  burning: {
    name: 'Burning',
    color: 0xff4400,
    duration: 3,
    tickRate: 1,
    tickDamage: 2,
  },
  frozen: {
    name: 'Frozen',
    color: 0x88ccff,
    duration: 3,
    speedMod: 0.4,
    nextHitMod: 1.5,
  },
  poisoned: {
    name: 'Poisoned',
    color: 0x44cc44,
    duration: 6,
    tickRate: 1,
    tickDamage: 1,
    stacks: true,
  },
  stunned: {
    name: 'Stunned',
    color: 0xffff00,
    duration: 1.5,
    canAct: false,
  },
};

export class StatusEffectSystem {
  /**
   * @param {import('../core/EventBus.js').EventBus} eventBus
   */
  constructor(eventBus) {
    this.eventBus = eventBus;
  }

  /**
   * Apply a status effect to an entity.
   * If the effect is already active and does not stack, refreshes the timer.
   * If it stacks (poisoned), increments the stack count and refreshes duration.
   *
   * @param {object} entity - Player or Enemy with statusEffects Map
   * @param {string} effectId - Key from STATUS_EFFECTS
   */
  apply(entity, effectId) {
    if (!entity.statusEffects) return;

    const config = STATUS_EFFECTS[effectId];
    if (!config) return;

    const existing = entity.statusEffects.get(effectId);

    if (existing) {
      if (config.stacks) {
        // Add a stack and refresh timer
        existing.stacks++;
        existing.timer = config.duration;
      } else {
        // Refresh duration
        existing.timer = config.duration;
      }
    } else {
      // Create new effect entry
      entity.statusEffects.set(effectId, {
        timer: config.duration,
        stacks: 1,
        tickTimer: config.tickRate || 0,
      });
    }

    // Apply visual tint immediately
    this._applyVisualTint(entity);
  }

  /**
   * Remove a status effect from an entity.
   * @param {object} entity - Player or Enemy with statusEffects Map
   * @param {string} effectId - Key from STATUS_EFFECTS
   */
  remove(entity, effectId) {
    if (!entity.statusEffects) return;
    entity.statusEffects.delete(effectId);
    this._applyVisualTint(entity);
  }

  /**
   * Tick all active effects on all entities. Applies damage ticks, removes expired effects.
   * @param {number} dt - Delta time in seconds
   * @param {object[]} entities - Array of entities (Player and Enemy instances)
   */
  update(dt, entities) {
    for (const entity of entities) {
      if (!entity.statusEffects || entity.statusEffects.size === 0) continue;

      // Skip dead entities
      if (entity.isAlive !== undefined && !entity.isAlive) continue;
      if (entity.hp !== undefined && entity.hp <= 0) continue;

      let needsTintUpdate = false;

      for (const [effectId, state] of entity.statusEffects) {
        const config = STATUS_EFFECTS[effectId];
        if (!config) continue;

        // Decrement main timer
        state.timer -= dt;

        // Handle damage ticks (burning, poisoned)
        if (config.tickRate && config.tickDamage) {
          state.tickTimer -= dt;
          if (state.tickTimer <= 0) {
            state.tickTimer += config.tickRate;

            // Calculate tick damage (multiply by stacks for poison)
            const dmg = config.tickDamage * (config.stacks ? state.stacks : 1);

            // Apply damage -- bypass invulnerability for DoT effects
            if (entity.hp !== undefined) {
              entity.hp = Math.max(0, entity.hp - dmg);
            }
          }
        }

        // Handle stun (stunned effect)
        if (effectId === 'stunned' && entity.stunTimer !== undefined) {
          // Keep stunTimer synced with status effect timer
          entity.stunTimer = Math.max(entity.stunTimer, state.timer);
        }

        // Handle frozen speed modifier
        // Speed modification is applied in the entity update methods

        // Visual flicker for burning
        if (effectId === 'burning' && entity.gfx) {
          const flicker = Math.sin(Date.now() * 0.015) > 0;
          if (flicker) {
            entity.gfx.tint = config.color;
          }
        }

        // Remove expired effects
        if (state.timer <= 0) {
          entity.statusEffects.delete(effectId);
          needsTintUpdate = true;

          // Clear stunTimer if stunned effect expired
          if (effectId === 'stunned' && entity.stunTimer !== undefined) {
            entity.stunTimer = 0;
          }
        }
      }

      if (needsTintUpdate) {
        this._applyVisualTint(entity);
      }
    }
  }

  /**
   * Get array of active effects on an entity.
   * @param {object} entity - Player or Enemy
   * @returns {{ id: string, remaining: number, stacks: number }[]}
   */
  getActiveEffects(entity) {
    const results = [];
    if (!entity.statusEffects) return results;

    for (const [effectId, state] of entity.statusEffects) {
      results.push({
        id: effectId,
        remaining: state.timer,
        stacks: state.stacks || 1,
      });
    }
    return results;
  }

  /**
   * Apply visual tinting based on active status effects.
   * Priority: burning > frozen > poisoned > stunned > default.
   * @param {object} entity
   */
  _applyVisualTint(entity) {
    if (!entity.gfx) return;

    // Don't override hit flash (red) or death tint
    if (entity.hitFlashTimer && entity.hitFlashTimer > 0) return;
    if (entity.state === 'dead' || entity.state === 'hurt') return;

    if (!entity.statusEffects || entity.statusEffects.size === 0) {
      entity.gfx.tint = 0xffffff;
      return;
    }

    // Priority order for tint color
    const priority = ['burning', 'frozen', 'poisoned', 'stunned'];
    for (const effectId of priority) {
      if (entity.statusEffects.has(effectId)) {
        entity.gfx.tint = STATUS_EFFECTS[effectId].color;
        return;
      }
    }

    entity.gfx.tint = 0xffffff;
  }

  /**
   * Check if an entity has the frozen effect. Used by CombatSystem for bonus damage.
   * @param {object} entity
   * @returns {boolean}
   */
  isFrozen(entity) {
    if (!entity.statusEffects) return false;
    return entity.statusEffects.has('frozen');
  }

  /**
   * Get the speed modifier for an entity based on active effects.
   * @param {object} entity
   * @returns {number} Multiplier (1.0 = no change, 0.4 = 60% slow)
   */
  getSpeedModifier(entity) {
    if (!entity.statusEffects) return 1.0;

    let mod = 1.0;

    // Frozen slows to 40% speed
    if (entity.statusEffects.has('frozen')) {
      const frozenConfig = STATUS_EFFECTS.frozen;
      mod *= frozenConfig.speedMod;
    }

    // Stunned = zero speed
    if (entity.statusEffects.has('stunned')) {
      mod = 0;
    }

    return mod;
  }
}
